// File parsing for the Collection import page.
//
// Two layouts are accepted, tried in this order:
//   1. "simple" — a plain table: one header row, one invoice per row. CSV or XLSX.
//      Columns are matched by name (case / spacing / punctuation insensitive), so the
//      column order doesn't matter and extra columns are ignored. This is also the
//      layout our own "Export to Excel" produces, so an export round-trips as an import.
//   2. "qbo" — the original QuickBooks "Estimates by Customer" export, where the customer
//      is a group header row and its invoices sit underneath it. Kept so existing files
//      still import unchanged.
//
// Everything is parsed in the browser; the file itself never leaves the app.

import * as XLSX from "xlsx";

export interface ParsedRow {
  customer: string;
  date: string; // YYYY-MM-DD
  number: string;
  status?: string;
  taxable?: number;
  tax?: number;
  amount: number;
  memo?: string;
}

export type ImportFormat = "simple" | "qbo";

export interface ParseOutcome {
  rows: ParsedRow[];
  format: ImportFormat;
  warnings: string[];
}

export class ParseError extends Error {}

type Field = keyof ParsedRow;

// Accepted spellings per column, compared after normalise() below.
const COLUMN_ALIASES: Record<Field, string[]> = {
  customer: ["customer", "customername", "client", "clientname", "name", "company", "companyname", "account"],
  date: ["date", "invoicedate", "issuedate", "issued", "documentdate", "docdate"],
  number: ["number", "no", "invoice", "invoiceno", "invoicenumber", "docno", "documentno", "documentnumber", "ref", "reference", "refno"],
  status: ["status", "state"],
  taxable: ["taxable", "taxableamount", "beforetax", "beforevat", "amountbeforetax", "subtotal", "net", "netamount", "excltax"],
  tax: ["tax", "taxamount", "vat", "vatamount"],
  amount: ["amount", "total", "totalamount", "grandtotal", "invoiceamount", "amountdue", "balance", "balancedue"],
  memo: ["memo", "note", "notes", "description", "details", "remark", "remarks", "comment", "comments"],
};

const REQUIRED_FIELDS: Field[] = ["customer", "date", "number", "amount"];

// The columns we document on the import page and write into the sample file. The `alsoAccepted`
// spellings come from COLUMN_ALIASES above, so the on-screen help can't drift from the parser.
export interface ColumnHelp {
  header: string;
  required: boolean;
  example: string;
  alsoAccepted: string;
  note: string;
}

export const COLUMN_HELP: ColumnHelp[] = [
  {
    header: "Customer", required: true, example: "Acme Co., Ltd.",
    alsoAccepted: "Client, Customer Name, Company",
    note: "Matched to an existing client by name, ignoring case. A name we haven't seen creates a new client.",
  },
  {
    header: "Date", required: true, example: "2026-01-15",
    alsoAccepted: "Invoice Date, Issue Date",
    note: "YYYY-MM-DD or DD/MM/YYYY. The invoice's issue date — credit terms are counted from it.",
  },
  {
    header: "Number", required: true, example: "INV-1001",
    alsoAccepted: "No., Invoice No., Invoice Number, Ref",
    note: "Identifies the invoice. The same number for the same client is always the same invoice.",
  },
  {
    header: "Amount", required: true, example: "10700.00",
    alsoAccepted: "Total, Amount Due, Balance",
    note: "Tax-inclusive total. This is the figure we chase and the one reminder emails quote.",
  },
  {
    header: "Before tax", required: false, example: "10000.00",
    alsoAccepted: "Taxable, Subtotal, Net",
    note: "Optional. Stored and shown in the Excel export.",
  },
  {
    header: "Tax", required: false, example: "700.00",
    alsoAccepted: "VAT, Tax Amount",
    note: "Optional. Stored and shown in the Excel export.",
  },
  {
    header: "Status", required: false, example: "open",
    alsoAccepted: "State",
    note: "Optional. Stored as-is. The one value that matters is \"accepted\" — it flags the invoice for Review instead of letting it be chased unnoticed.",
  },
  {
    header: "Memo", required: false, example: "January retainer",
    alsoAccepted: "Note, Description, Remark",
    note: "Optional. Replaces the memo held in the app — a blank cell clears it.",
  },
];

/** The columns we advertise in the UI and write into the downloadable sample file. */
export const TEMPLATE_HEADERS = COLUMN_HELP.map((c) => c.header);

/** One demo row, matching TEMPLATE_HEADERS position for position. */
export const TEMPLATE_ROW = COLUMN_HELP.map((c) => c.example);

/** Lowercase and drop everything that isn't a letter or digit: "Invoice No." === "invoice_no". */
function normalise(v: unknown): string {
  return String(v ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function text(v: unknown): string {
  return v == null ? "" : String(v).trim();
}

// ------------------------------------------------------------------ entry point

/** Read a CSV / XLSX / XLS file and return invoice rows, whichever of the two layouts it uses. */
export async function parseImportFile(file: File): Promise<ParseOutcome> {
  const aoa = await readFirstSheet(file);
  if (!aoa.length) throw new ParseError("The file is empty.");

  const simple = parseSimple(aoa);
  if (simple) return simple;

  const qboRows = parseQbo(aoa);
  if (qboRows.length) return { rows: qboRows, format: "qbo", warnings: [] };

  throw new ParseError(
    "Couldn't find a header row. The first row should name the columns — at least " +
      "Customer, Date, Number and Amount. Download the template below if you're unsure of the layout.",
  );
}

async function readFirstSheet(file: File): Promise<unknown[][]> {
  const isText = /\.(csv|tsv|txt)$/i.test(file.name) || file.type === "text/csv";
  const wb = isText
    ? XLSX.read(await file.text(), { type: "string", raw: false })
    : XLSX.read(await file.arrayBuffer(), { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) throw new ParseError("The file has no worksheets.");
  return XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: false, defval: null });
}

// ------------------------------------------------------------------ simple layout

/** Returns null when there's no usable header row, so the caller can try the QBO layout. */
function parseSimple(aoa: unknown[][]): ParseOutcome | null {
  // The header is normally row 1, but allow a few title/blank rows above it.
  let headerIdx = -1;
  let colOf: Partial<Record<Field, number>> = {};
  for (let i = 0; i < Math.min(aoa.length, 15); i++) {
    const mapped = mapHeader(aoa[i] || []);
    if (mapped) { headerIdx = i; colOf = mapped; break; }
  }
  if (headerIdx < 0) return null;

  const missing = REQUIRED_FIELDS.filter((f) => colOf[f] === undefined);
  if (missing.length) {
    throw new ParseError(
      `The file is missing required column${missing.length > 1 ? "s" : ""}: ${missing.join(", ")}. ` +
        `Expected a header row with ${TEMPLATE_HEADERS.slice(0, 4).join(", ")} (Status, Taxable, Tax and Memo are optional).`,
    );
  }

  const rows: ParsedRow[] = [];
  let badDate = 0, badAmount = 0, incomplete = 0;

  for (let i = headerIdx + 1; i < aoa.length; i++) {
    const raw = aoa[i] || [];
    const cell = (f: Field) => {
      const idx = colOf[f];
      return idx === undefined ? null : raw[idx];
    };

    const customer = text(cell("customer"));
    const number = text(cell("number"));

    // Blank spacer row, or the totals line some exports append at the bottom.
    if (!customer && !number) continue;
    if (!customer || !number) { incomplete++; continue; }
    if (/^(grand\s+)?totals?\b/i.test(customer)) continue;

    const date = parseDateToIso(cell("date"));
    if (!date) { badDate++; continue; }

    const amount = parseNumber(cell("amount"));
    if (amount === null) { badAmount++; continue; }

    rows.push({
      customer,
      date,
      number,
      status: text(cell("status")) || undefined,
      taxable: parseNumber(cell("taxable")) ?? 0,
      tax: parseNumber(cell("tax")) ?? 0,
      amount,
      memo: text(cell("memo")) || undefined,
    });
  }

  const warnings: string[] = [];
  if (incomplete) warnings.push(`${incomplete} row(s) skipped — customer or number was blank.`);
  if (badDate) warnings.push(`${badDate} row(s) skipped — the date couldn't be read.`);
  if (badAmount) warnings.push(`${badAmount} row(s) skipped — the amount couldn't be read.`);

  return { rows, format: "simple", warnings };
}

/** Map a candidate header row to column indexes; null when it doesn't look like a header. */
function mapHeader(row: unknown[]): Partial<Record<Field, number>> | null {
  const out: Partial<Record<Field, number>> = {};
  for (let c = 0; c < row.length; c++) {
    const key = normalise(row[c]);
    if (!key) continue;
    for (const field of Object.keys(COLUMN_ALIASES) as Field[]) {
      if (out[field] === undefined && COLUMN_ALIASES[field].includes(key)) { out[field] = c; break; }
    }
  }
  // A header must at least name the customer plus one of number/amount — otherwise this is
  // a QBO group header ("Date"/"Number" only) or a stray title row.
  const looksLikeHeader = out.customer !== undefined && (out.number !== undefined || out.amount !== undefined);
  return looksLikeHeader ? out : null;
}

// ------------------------------------------------------- QBO layout (behaviour unchanged)

/** Parse a QBO "Estimates by Customer" worksheet: customer group headers with detail rows beneath. */
function parseQbo(aoa: unknown[][]): ParsedRow[] {
  const out: ParsedRow[] = [];
  let currentCustomer = "";

  // Find the header row — 'Date' in column B and 'Number' in column C.
  let dataStartIdx = -1;
  for (let i = 0; i < Math.min(aoa.length, 20); i++) {
    const r = aoa[i] || [];
    if (normalise(r[1]) === "date" && normalise(r[2]) === "number") { dataStartIdx = i + 1; break; }
  }
  if (dataStartIdx < 0) return out;

  for (let i = dataStartIdx; i < aoa.length; i++) {
    const row = aoa[i] || [];
    const colA = text(row[0]);
    const colB = text(row[1]);

    // Customer header: col A populated, col B empty, not a "Total for" subtotal.
    if (colA && !colB && !/^total for /i.test(colA)) { currentCustomer = colA; continue; }
    if (/^total for /i.test(colA)) continue;

    // Detail row: column B is a date, column C is the estimate number.
    if (!colB || !row[2]) continue;
    const isoDate = parseDateToIso(colB);
    if (!isoDate) continue;

    out.push({
      customer: currentCustomer,
      date: isoDate,
      number: text(row[2]),
      status: text(row[3]) || undefined,
      taxable: parseNumber(row[4]) ?? 0,
      tax: parseNumber(row[5]) ?? 0,
      amount: parseNumber(row[6]) ?? 0,
      memo: text(row[7]) || undefined,
    });
  }
  return out;
}

// ------------------------------------------------------------------ cell helpers

/** Money cell → number. Handles thousands separators, currency symbols and (1,234) negatives. */
export function parseNumber(v: unknown): number | null {
  if (v == null || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  if (!s) return null;
  const negative = /^\(.*\)$/.test(s) || s.startsWith("-");
  const digits = s.replace(/[^0-9.]/g, "");
  if (!digits || digits === ".") return null;
  const n = parseFloat(digits);
  if (!Number.isFinite(n)) return null;
  return negative ? -n : n;
}

/** Date cell → YYYY-MM-DD. Accepts ISO, DD/MM/YYYY (QBO Thailand), "15 Jan 2026" and Excel serials. */
export function parseDateToIso(v: unknown): string | null {
  if (v == null || v === "") return null;

  // Excel stores dates as day numbers when the cell isn't formatted as a date.
  if (typeof v === "number") return fromExcelSerial(v);

  const s = String(v).trim();
  if (!s) return null;

  // ISO first — unambiguous, and what the template uses.
  const iso = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (iso) return ymd(iso[1], iso[2], iso[3]);

  // Day-first, matching the QBO export (Thailand locale).
  const dmy = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (dmy) {
    let y = dmy[3];
    if (y.length === 2) y = (Number(y) > 50 ? "19" : "20") + y;
    return ymd(y, dmy[2], dmy[1]);
  }

  if (/^\d+(\.\d+)?$/.test(s)) return fromExcelSerial(Number(s));

  // "15 Jan 2026", "Jan 15, 2026", …
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return ymd(String(d.getFullYear()), String(d.getMonth() + 1), String(d.getDate()));
  }
  return null;
}

function ymd(y: string, m: string, d: string): string | null {
  const mo = Number(m), day = Number(d);
  if (!(mo >= 1 && mo <= 12) || !(day >= 1 && day <= 31)) return null;
  return `${y.padStart(4, "0")}-${String(mo).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function fromExcelSerial(serial: number): string | null {
  if (!Number.isFinite(serial) || serial < 1 || serial > 60000) return null;
  const parsed = XLSX.SSF.parse_date_code(serial);
  if (!parsed) return null;
  return ymd(String(parsed.y), String(parsed.m), String(parsed.d));
}

// ------------------------------------------------------------------ template

/** Header + the one demo row, as a worksheet-ready array. */
export function templateAoa(): (string | number)[][] {
  return [TEMPLATE_HEADERS, TEMPLATE_ROW];
}

/** A ready-to-fill CSV sample: header row plus one demo row. */
export function templateCsv(): string {
  const quote = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return [TEMPLATE_HEADERS, TEMPLATE_ROW].map((r) => r.map(quote).join(",")).join("\r\n") + "\r\n";
}

/** The same sample as a real .xlsx workbook, for people who'd rather start in Excel. */
export function templateXlsxBlob(): Blob {
  const ws = XLSX.utils.aoa_to_sheet(templateAoa());
  ws["!cols"] = TEMPLATE_HEADERS.map((h) => ({ wch: Math.max(12, h.length + 6) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Invoices");
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" }) as ArrayBuffer;
  return new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}
